package com.swetdot.swetgram.protocol

import kotlinx.serialization.json.Json

/**
 * Wire types mirroring `docs/api/openapi.yaml` v1.0.0.
 *
 * RULES FOR THIS MODULE:
 *  - The contract is the source of truth. Do not add a field here that the OpenAPI document
 *    does not define, and do not rename one. Update the contract first.
 *  - Every property carries an explicit `@SerialName` in the contract's snake_case. Kotlin
 *    properties stay camelCase; the wire stays snake_case. A global naming strategy would do
 *    this implicitly, but explicit names are greppable and cannot silently drift.
 *  - These are DTOs, not domain models. `IMPLEMENTATION_NOTES.md` §10 requires the two stay
 *    separate on every client, so `:shared` maps these into its own domain types rather than
 *    passing them to the UI.
 */

/** REST prefix. Every path in the contract hangs off this. */
const val API_PREFIX: String = "/api/v1"

/**
 * `format: uuid` and `format: date-time` are carried as Strings deliberately.
 *
 * The DTO layer's job is to reproduce the wire exactly; parsing belongs at the boundary into
 * the domain model. Keeping them as Strings also keeps `:protocol` free of kotlinx-datetime,
 * whose 0.7/0.8 split against Exposed's compat variant is a known trap, and free of
 * `java.util.UUID`, which does not exist on Kotlin/Native.
 *
 * Never compare Timestamps as strings for ordering -- convert first.
 */
typealias Uuid = String
typealias Timestamp = String

/** `ResourceVersion`: integer, minimum 1. A stale write returns 409 `version_conflict`. */
typealias ResourceVersion = Int

/** Constraints the contract states, mirrored so clients can validate before a round trip. */
object Constraints {
    const val USERNAME_MIN = 3
    const val USERNAME_MAX = 24
    const val USERNAME_PATTERN = "^[a-z0-9_]+$"

    const val PASSWORD_MIN = 10
    const val PASSWORD_MAX = 128

    const val DISPLAY_NAME_MIN = 1
    const val DISPLAY_NAME_MAX = 80

    const val BIO_MAX = 160
    const val LOCATION_MAX = 100
    const val MESSAGE_MAX = 4096
    const val EMAIL_MAX = 254

    /** Reserved in every namespace; registration and username changes must reject these. */
    val RESERVED_USERNAMES: Set<String> = setOf(
        "admin", "administrator", "support", "security", "swetgram", "official",
        "moderator", "system", "help", "api", "www", "mail",
    )

    /** Minimum 10 characters with at least one letter and one digit. */
    fun isPasswordValid(password: String): Boolean =
        password.length in PASSWORD_MIN..PASSWORD_MAX &&
            password.any { it.isLetter() } &&
            password.any { it.isDigit() }

    fun isUsernameValid(username: String): Boolean =
        username.length in USERNAME_MIN..USERNAME_MAX &&
            username.all { it in 'a'..'z' || it in '0'..'9' || it == '_' } &&
            username !in RESERVED_USERNAMES
}

/** Headers the contract requires on every request. */
object ClientHeaders {
    /** Contract enum is exactly `ios` | `android`. */
    const val PLATFORM = "X-Client-Platform"
    const val VERSION = "X-Client-Version"
    const val BUILD = "X-Client-Build"
    /** Spelled with a capital ID in the contract. HTTP header matching is case-insensitive. */
    const val REQUEST_ID = "X-Request-ID"
    /** REQUIRED on sendMessage; a client-generated UUID. Records are kept for 7 days. */
    const val IDEMPOTENCY_KEY = "Idempotency-Key"
    const val IF_NONE_MATCH = "If-None-Match"
}

/**
 * The ONE Json configuration, shared by server and every client.
 *
 *  - `ignoreUnknownKeys` so a field added in a later minor version does not break an already
 *    shipped client. The contract's own change policy calls field additions backward
 *    compatible, which is only true if clients are configured this way.
 *  - `explicitNulls = false` because the contract marks many fields nullable; omitting them
 *    keeps frames small and matches how the server serialises absent values.
 *  - `encodeDefaults` so a defaulted field is transmitted rather than assumed.
 *  - `coerceInputValues` so an enum constant added later falls back to a declared default
 *    instead of throwing. Every wire enum below therefore has an UNKNOWN member.
 */
val ProtocolJson: Json = Json {
    ignoreUnknownKeys = true
    explicitNulls = false
    encodeDefaults = true
    coerceInputValues = true
}
